import { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import type { AdminProduct } from '../types/models/AdminProduct';
import type { ApiResponseAdminProductList } from '../types/api/ApiResponseAdminProduct';

interface UseAdminProductsReturn {
  products: AdminProduct[];
  loading: boolean;
  /** Human-readable error message (the backend's JSend `message`), or null. */
  error: string | null;
  refetch: () => Promise<void>;
  /**
   * Replace one product's row in place (matched by `id`) from a price/category mutation response, so
   * an edit updates its row without a full refetch (which would flash the loading spinner over the
   * table). The set-default-price route returns only { product_id, default_unit_price }, so callers
   * merge that into the existing row before calling this.
   */
  applyProduct: (updated: AdminProduct) => void;
}

// GET /admin/products lists the whole catalog with each product's category embedded, ordered by
// category (id = display order) then product label — the page renders category groups by walking
// that order. Staff-only on the backend; the page mirrors the gate. useApiFetch injects the bearer
// token (and, irrelevantly here, the impersonation header — these admin routes authorize on the
// real staff user and take explicit ids).
//
// `enabled` (default true) defers the fetch so a non-staff user who lands on the route directly
// (and is shown <NotFound />) never fires the backend-rejected admin call: the page passes its own
// `is_ancalagon_staff` check in, matching the page's "reveal nothing" defense-in-depth.
export const useAdminProducts = (enabled = true): UseAdminProductsReturn => {
  const [products, setProducts] = useState<AdminProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const auth = useAuth();
  const apiFetch = useApiFetch();

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiFetch('/admin/products');
      // Branch on the JSend `body.status`, NEVER `response.ok`: the backend maps controller
      // exceptions to `status: 'error'` at HTTP 200, so `response.ok` can be true on a failure.
      const body = (await response.json().catch(() => null)) as ApiResponseAdminProductList | null;
      if (body?.status === 'success' && body.data?.products) {
        setProducts(body.data.products);
      } else {
        setError(body?.message ?? 'Failed to load products');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while fetching products');
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  const applyProduct = useCallback((updated: AdminProduct) => {
    setProducts((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
  }, []);

  useEffect(() => {
    if (enabled && auth.isAuthenticated) {
      void fetchProducts();
    }
  }, [enabled, auth.isAuthenticated, fetchProducts]);

  return { products, loading, error, refetch: fetchProducts, applyProduct };
};
